import { createFileRoute, Link, useLoaderData, useNavigate } from "@tanstack/react-router";
import { ModeToggle } from "@/components/ModeToggle";
import { useState, useEffect, useRef, lazy, Suspense } from "react";

import { FinlandFlagIcon } from "@/components/icons/FinlandFlagIcon";
import { useCity } from "@/context/CityContext";
import { CITIES } from "@/data/cities";
import { useResolvedCity, usePublicCities } from "@/hooks/use-public-cities";
import { type Item } from "@/data/places";
import { useListedPlaces } from "@/hooks/use-public-places";
import { useNearestSort } from "@/hooks/useNearestSort";

import { usePublicSettings, type SiteSettings } from "@/hooks/use-public-settings";
import { usePublicCarousels } from "@/hooks/use-public-carousels";
import { PlaceCard } from "@/components/PlaceCard";
import { Logo } from "@/components/Logo";
import { HomeSectionHeader, RailEdgeFade, useRailOverflow } from "@/components/RailArrows";

import { Search, Sparkles, Loader2 } from "lucide-react";
const TopPicksCarousel = lazy(() => import("@/components/TopPicksCarousel"));
const CategoryShowcase = lazy(() => import("@/components/CategoryShowcase").then(m => ({ default: m.CategoryShowcase })));
import { MapMarkerPinIcon } from "@/components/icons/MapMarkerPinIcon";
import { SearchOverlay } from "@/components/SearchOverlay";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPublicDistricts } from "@/lib/districts.functions";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/config";
import { AutoT } from "@/i18n/AutoT";
import { useAutoTranslate } from "@/i18n/useAutoTranslate";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";
import { getSeoHappeningItems } from "@/lib/seo.functions";
import { IpGreeting } from "@/components/IpGreeting";
import { optimizeImg, optimizeSrcSet } from "@/lib/img";
import { fetchHomeSeed } from "@/lib/home-seed";
import { useSeedQuery } from "@/hooks/use-seed-query";




import { Index } from "@/components/home/HomePage";
export const Route = createFileRoute("/")({
  head: ({ loaderData }) => {
    const heroImages: string[] = Array.isArray((loaderData as any)?.hero_images)
      ? ((loaderData as any).hero_images as string[])
      : [];
    const events: Array<{ slug: string; name: string; startDate: string | null; city: string | null; venue: string | null }> =
      Array.isArray((loaderData as any)?.events) ? ((loaderData as any).events as any[]) : [];
    const firstHero = heroImages[0];
    const ogImage = firstHero
      ? (firstHero.startsWith("http") ? firstHero : `https://whatfinland.com${firstHero}`)
      : "https://whatfinland.com/icon-512.png";
    const title = "Finland Guide: Restaurants, Saunas & Events | what Finland";
    const description =
      "Finland Guide. Discover the best local experiences — restaurants, saunas, events and hidden gems across Finnish cities. Select a city and start your journey!";
    const upcomingItemList = events.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Upcoming events in Finland",
      url: "https://whatfinland.com/",
      numberOfItems: events.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: events.map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://whatfinland.com/happening/${e.slug}`,
        name: e.name,
        ...(e.startDate || e.city || e.venue
          ? {
              item: {
                "@type": "Event",
                name: e.name,
                url: `https://whatfinland.com/happening/${e.slug}`,
                ...(e.startDate ? { startDate: e.startDate } : {}),
                ...(e.venue || e.city
                  ? { location: { "@type": "Place", name: e.venue ?? e.city, ...(e.city ? { address: { "@type": "PostalAddress", addressLocality: e.city, addressCountry: "FI" } } : {}) } }
                  : {}),
              },
            }
          : {}),
      })),
    } : null;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "https://whatfinland.com/" },
        ...buildLocaleMeta("en"),
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: "what Finland — Helsinki city guide" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "keywords", content: "Helsinki guide, things to do in Helsinki, best restaurants Helsinki, Finland travel guide, Finnish sauna, Helsinki events, Turku, Tampere, Hanko, Porvoo, what Finland" },
      ],
      links: [
        { rel: "canonical", href: "https://whatfinland.com/" },
        ...buildHreflangLinks("/"),
        ...(firstHero
          ? [
              {
                rel: "preload",
                as: "image",
                href: optimizeImg(firstHero, 720, { quality: 65, aspect: 16 / 9 }),
                // React 19 requires camelCase for these `<link rel=preload as=image>`
                // attributes; lowercase produces "Invalid DOM property" warnings.
                imageSrcSet: optimizeSrcSet(firstHero, [360, 480, 720, 1024], { quality: 60, aspect: 16 / 9 }),
                imageSizes: "(max-width: 640px) 100vw, 1024px",
                fetchPriority: "high",
              } as any,

            ]
          : []),
      ],
      ...(upcomingItemList
        ? { scripts: [{ type: "application/ld+json", children: JSON.stringify(upcomingItemList) }] }
        : {}),
    };
  },
  loader: async ({ context }) => {
    // Everything the first paint needs, fetched in parallel. Seeding cities +
    // the first page of places into the SSR payload means the above-the-fold
    // content ships inside the HTML instead of waiting for hydration and two
    // extra round trips (big Speed Index win).
    const settingsPromise = context.queryClient.fetchQuery({
      queryKey: ["public-site-settings"],
      queryFn: async () => {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data, error } = await supabase.from("site_settings").select("key, value");
        if (error) throw error;
        const obj: any = {};
        for (const row of data ?? []) obj[(row as any).key] = (row as any).value;
        return obj;
      },
    });

    // Upcoming events power the homepage ItemList JSON-LD so crawlers see
    // real events in the raw HTML even though the visible carousel hydrates
    // client-side.
    const eventsPromise = getSeoHappeningItems({ data: { limit: 12 } })
      .then((res: any) =>
        (res.items ?? []).map((e: any) => ({
          slug: e.slug,
          name: e.name,
          startDate: e.startDate ?? null,
          city: e.city ?? null,
          venue: e.venue ?? null,
        })),
      )
      .catch(() => [] as Array<{ slug: string; name: string; startDate: string | null; city: string | null; venue: string | null }>);

    const seedPromise = fetchHomeSeed().catch(() => ({ cities: [], places: [] }));

    const [settings, events, seed] = await Promise.all([settingsPromise, eventsPromise, seedPromise]);

    return {
      hero_images: Array.isArray(settings?.hero_images) ? settings.hero_images : [],
      settings: (settings ?? {}) as Record<string, unknown>,
      events,
      seed,
    };
  },

  component: Index,
});
